import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";

const EMPTY = {
  title: "",
  subtitle: "",
  tagline: "",
  description: "",
  cover_image: "",
  preview_url: "",
  purchase_links: [],
  endorsements: [],
};

export default function AdminBook() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/book")
      .then((r) =>
        setForm({
          title: r.data.title || "",
          subtitle: r.data.subtitle || "",
          tagline: r.data.tagline || "",
          description: r.data.description || "",
          cover_image: r.data.cover_image || "",
          preview_url: r.data.preview_url || "",
          purchase_links: (r.data.purchase_links || []).slice(0, 3),
          endorsements: (r.data.endorsements || []).slice(0, 6),
        }),
      )
      .finally(() => setLoading(false));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const updateLink = (i, key, val) =>
    setForm((f) => {
      const next = [...f.purchase_links];
      next[i] = { ...next[i], [key]: val };
      return { ...f, purchase_links: next };
    });

  const addLink = () =>
    setForm((f) =>
      f.purchase_links.length >= 3
        ? f
        : { ...f, purchase_links: [...f.purchase_links, { label: "", url: "" }] },
    );

  const removeLink = (i) =>
    setForm((f) => ({
      ...f,
      purchase_links: f.purchase_links.filter((_, idx) => idx !== i),
    }));

  const updateEndorsement = (i, key, val) =>
    setForm((f) => {
      const next = [...f.endorsements];
      next[i] = { ...next[i], [key]: val };
      return { ...f, endorsements: next };
    });

  const addEndorsement = () =>
    setForm((f) =>
      f.endorsements.length >= 6
        ? f
        : {
            ...f,
            endorsements: [
              ...f.endorsements,
              { quote: "", reviewer_name: "", reviewer_title: "" },
            ],
          },
    );

  const removeEndorsement = (i) =>
    setForm((f) => ({
      ...f,
      endorsements: f.endorsements.filter((_, idx) => idx !== i),
    }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/book", {
        ...form,
        purchase_links: form.purchase_links.filter((l) => l.label && l.url),
        endorsements: form.endorsements.filter(
          (e) => e.quote && e.reviewer_name,
        ),
      });
      toast.success("Book details saved");
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || "Save failed",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20" data-testid="admin-book-loading">
        <p className="text-ink-soft">Loading…</p>
      </div>
    );
  }

  return (
    <div className="bg-paper min-h-[80vh]" data-testid="admin-book-page">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-16 pb-24">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink-soft hover:text-terracotta mb-8"
          data-testid="admin-book-back-link"
        >
          <ArrowLeft size={14} /> Dashboard
        </Link>

        <p className="text-xs uppercase tracking-[0.22em] text-terracotta mb-4">
          Book Details
        </p>
        <h1 className="font-serif-display text-3xl lg:text-4xl text-ink tracking-tight">
          The Book page
        </h1>
        <p className="mt-3 text-sm text-ink-soft">
          These details appear at <span className="font-mono text-ink">/book</span>.
        </p>

        <div className="mt-12 space-y-8">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">
              Title
            </label>
            <input
              value={form.title}
              onChange={set("title")}
              data-testid="admin-book-title-input"
              className="w-full bg-transparent border-b border-rule focus:border-ink py-3 text-2xl font-serif-display text-ink outline-none"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">
              Subtitle
            </label>
            <input
              value={form.subtitle}
              onChange={set("subtitle")}
              data-testid="admin-book-subtitle-input"
              className="w-full bg-transparent border-b border-rule focus:border-ink py-3 text-sm text-ink outline-none"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">
              Tagline (one-line hook)
            </label>
            <textarea
              rows={2}
              value={form.tagline}
              onChange={set("tagline")}
              data-testid="admin-book-tagline-input"
              className="w-full bg-paper border border-rule px-4 py-3 text-base text-ink leading-relaxed outline-none focus:border-ink font-serif-display italic"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">
              Description
            </label>
            <textarea
              rows={6}
              value={form.description}
              onChange={set("description")}
              data-testid="admin-book-description-input"
              className="w-full bg-paper border border-rule px-4 py-3 text-base text-ink leading-relaxed outline-none focus:border-ink"
            />
          </div>

          <ImageUploader
            value={form.cover_image}
            onChange={(v) => setForm((f) => ({ ...f, cover_image: v }))}
            label="Cover Image"
            testIdPrefix="admin-book-cover"
          />

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">
              Preview URL (Look Inside, etc.)
            </label>
            <input
              value={form.preview_url}
              onChange={set("preview_url")}
              data-testid="admin-book-preview-input"
              className="w-full bg-transparent border-b border-rule focus:border-ink py-3 text-sm text-ink outline-none"
              placeholder="https://…"
            />
          </div>

          <div className="border-t border-rule pt-8">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs uppercase tracking-[0.2em] text-ink-soft">
                Purchase Links (max 3)
              </p>
              <button
                onClick={addLink}
                disabled={form.purchase_links.length >= 3}
                data-testid="admin-book-add-link"
                className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] text-terracotta hover:text-ink disabled:opacity-40"
              >
                <Plus size={14} /> Add link
              </button>
            </div>

            {form.purchase_links.length === 0 && (
              <p className="text-sm text-ink-soft italic">No purchase links yet.</p>
            )}

            <div className="space-y-4">
              {form.purchase_links.map((l, i) => (
                <div
                  key={i}
                  data-testid={`admin-book-link-row-${i}`}
                  className="grid grid-cols-12 gap-3 items-center"
                >
                  <input
                    value={l.label}
                    onChange={(e) => updateLink(i, "label", e.target.value)}
                    placeholder="Label (e.g. Amazon)"
                    data-testid={`admin-book-link-label-${i}`}
                    className="col-span-3 bg-paper border border-rule px-3 py-2 text-sm text-ink outline-none focus:border-ink"
                  />
                  <input
                    value={l.url}
                    onChange={(e) => updateLink(i, "url", e.target.value)}
                    placeholder="https://…"
                    data-testid={`admin-book-link-url-${i}`}
                    className="col-span-8 bg-paper border border-rule px-3 py-2 text-sm text-ink outline-none focus:border-ink"
                  />
                  <button
                    onClick={() => removeLink(i)}
                    data-testid={`admin-book-link-remove-${i}`}
                    className="col-span-1 text-ink-soft hover:text-terracotta justify-self-end"
                    title="Remove"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-rule pt-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-ink-soft">
                  Endorsements (max 6)
                </p>
                <p className="text-xs text-ink-soft mt-1 normal-case tracking-normal">
                  Short quotes from readers, parents, or fellow authors.
                </p>
              </div>
              <button
                onClick={addEndorsement}
                disabled={form.endorsements.length >= 6}
                data-testid="admin-book-add-endorsement"
                className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] text-terracotta hover:text-ink disabled:opacity-40"
              >
                <Plus size={14} /> Add endorsement
              </button>
            </div>

            {form.endorsements.length === 0 && (
              <p className="text-sm text-ink-soft italic">No endorsements yet.</p>
            )}

            <div className="space-y-6">
              {form.endorsements.map((e, i) => (
                <div
                  key={i}
                  data-testid={`admin-book-endorsement-row-${i}`}
                  className="border border-rule p-4 bg-paper-2/30 relative"
                >
                  <button
                    onClick={() => removeEndorsement(i)}
                    data-testid={`admin-book-endorsement-remove-${i}`}
                    className="absolute top-2 right-2 text-ink-soft hover:text-terracotta"
                    title="Remove endorsement"
                  >
                    <X size={16} />
                  </button>
                  <textarea
                    value={e.quote}
                    onChange={(ev) => updateEndorsement(i, "quote", ev.target.value)}
                    rows={3}
                    placeholder="The quote…"
                    data-testid={`admin-book-endorsement-quote-${i}`}
                    className="w-full bg-paper border border-rule px-3 py-2 text-base text-ink leading-relaxed outline-none focus:border-ink font-serif-display italic"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <input
                      value={e.reviewer_name}
                      onChange={(ev) =>
                        updateEndorsement(i, "reviewer_name", ev.target.value)
                      }
                      placeholder="Reviewer name (e.g. Sarah M.)"
                      data-testid={`admin-book-endorsement-name-${i}`}
                      className="bg-paper border border-rule px-3 py-2 text-sm text-ink outline-none focus:border-ink"
                    />
                    <input
                      value={e.reviewer_title}
                      onChange={(ev) =>
                        updateEndorsement(i, "reviewer_title", ev.target.value)
                      }
                      placeholder="Reviewer title (optional, e.g. Parent of two)"
                      data-testid={`admin-book-endorsement-title-${i}`}
                      className="bg-paper border border-rule px-3 py-2 text-sm text-ink outline-none focus:border-ink"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-6 border-t border-rule">
            <button
              onClick={save}
              disabled={saving || !form.title}
              data-testid="admin-book-save-btn"
              className="px-6 py-3 bg-terracotta text-paper text-xs tracking-[0.18em] uppercase hover:bg-ink transition-colors disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save Book Details"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
